(() => {
  const GLOBAL = 'BasicHeroKiimaMotionV5';
  const SELECTOR = '[data-basic-hero-kiima]';
  const LEGACY_GLOBALS = [
    'BasicHeroKiimaMotionV2',
    'BasicHeroKiimaMotionV3',
    'BasicHeroKiimaMotionV4',
    GLOBAL
  ];

  LEGACY_GLOBALS.forEach((name) => {
    if (window[name]?.destroyAll) window[name].destroyAll(document);
  });

  const instances = new WeakMap();
  const mobileVideoQuery = window.matchMedia('(max-width: 899px)');
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  function initHero(root) {
    if (!root || instances.has(root)) return;

    const video = root.querySelector('[data-basic-hero-video]');
    const stage = root.querySelector('.basic-hero-kiima__stage');
    const pageWrapper = root.closest('.page-wrapper');
    const reducedQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const constrainedNetwork = Boolean(connection && connection.saveData);
    const scrubEnabled = root.dataset.scrub === 'true';
    const disposers = [];

    let duration = 0;
    let scrollRange = 1;
    let scrollSurface = window;
    let progressFrame = 0;
    let seekFrame = 0;
    let metricsDirty = true;
    let targetTime = 0;
    let lastSeekAt = 0;
    let ready = false;
    let loadStarted = false;
    let observer = null;
    let resizeObserver = null;
    let retryCount = 0;
    let retryTimer = 0;
    let paintFallbackTimer = 0;
    let mediaError = false;
    let errorFrame = 0;
    let playbackGeneration = 0;

    // Resolve before our first DOM writes; all later geometry work is rAF-batched.
    scrollSurface = resolveScrollSurface();
    root.dataset.motionReady = 'true';
    root.dataset.motionState = 'initializing';

    const sourceForViewport = () => {
      const desktop = root.dataset.videoSrcDesktop || '';
      const mobile = root.dataset.videoSrcMobile || desktop;
      return mobileVideoQuery.matches ? mobile : desktop;
    };

    const listen = (target, event, handler, options) => {
      target.addEventListener(event, handler, options);
      disposers.push(() => target.removeEventListener(event, handler, options));
    };

    function resolveScrollSurface() {
      // Horizon 4 uses this scrollport on desktop, but window still scrolls on mobile.
      // Keep the same surface while a drawer temporarily changes overflow to hidden.
      if (pageWrapper && pageWrapper.clientHeight > 0 &&
          pageWrapper.scrollHeight > pageWrapper.clientHeight + 1 &&
          /^(auto|scroll|overlay|hidden)$/.test(window.getComputedStyle(pageWrapper).overflowY)) {
        return pageWrapper;
      }
      return window;
    }

    function getScrollMetrics() {
      if (scrollSurface !== window) {
        return {
          position: scrollSurface.scrollTop,
          top: scrollSurface.getBoundingClientRect().top + scrollSurface.clientTop,
          height: scrollSurface.clientHeight
        };
      }
      return {
        position: window.scrollY || window.pageYOffset || 0,
        top: 0,
        height: window.innerHeight
      };
    }

    function getStickyTop(metrics) {
      const header = document.querySelector('.shopify-section-header, header');
      if (!header) return 0;

      return clamp(header.getBoundingClientRect().bottom - metrics.top, 0, metrics.height * 0.35);
    }

    function setRootProperty(name, value) {
      if (root.style.getPropertyValue(name) === value) return false;
      root.style.setProperty(name, value);
      return true;
    }

    function updateMetrics() {
      if (metricsDirty) {
        scrollSurface = resolveScrollSurface();
        metricsDirty = false;
      }

      // Read the complete rendered geometry before changing any styles.
      const metrics = getScrollMetrics();
      const stickyTop = getStickyTop(metrics);
      const rootTop = root.getBoundingClientRect().top;
      const rootHeight = root.offsetHeight;
      const stageHeight = stage ? stage.offsetHeight : metrics.height;
      return { ...metrics, stickyTop, rootTop, rootHeight, stageHeight };
    }

    function requestMetricsUpdate() {
      metricsDirty = true;
      requestUpdate();
    }

    function getHeroProgress(metrics) {
      if (!scrubEnabled || !stage) return 0;

      const rootStart = metrics.position + metrics.rootTop - metrics.top;
      const scrollStart = rootStart - metrics.stickyTop;
      // Same timeline, using the actual stage on the current scroll surface.
      scrollRange = Math.max(1, metrics.rootHeight - metrics.stageHeight);
      return clamp((metrics.position - scrollStart) / scrollRange, 0, 1);
    }

    function applyGeometry(metrics) {
      const surfaceName = scrollSurface === window ? 'window' : 'page-wrapper';
      if (root.dataset.motionScrollSurface !== surfaceName) {
        root.dataset.motionScrollSurface = surfaceName;
      }

      if (setRootProperty('--hero-sticky-top', `${metrics.stickyTop.toFixed(2)}px`)) {
        // The stage height depends on this property. Read its rendered result on
        // the next frame, not synchronously after invalidating layout here.
        requestUpdate();
      }
    }

    function setVisualProgress(heroProgress) {
      setRootProperty('--hero-progress', heroProgress.toFixed(4));
      setRootProperty('--hero-cue-opacity', Math.max(0, 1 - heroProgress * 8).toFixed(3));
    }

    function revealPaintedFrame() {
      if (!video || mediaError || ready || reducedQuery.matches || constrainedNetwork || video.readyState < 2) return;
      if (Math.abs(video.currentTime - targetTime) > 0.12) return;

      const generation = playbackGeneration;
      const reveal = () => {
        if (generation !== playbackGeneration || mediaError || ready || reducedQuery.matches || constrainedNetwork) return;
        ready = true;
        window.clearTimeout(paintFallbackTimer);
        paintFallbackTimer = 0;
        root.classList.remove('is-static');
        root.classList.add('is-video-ready');
        root.dataset.motionState = 'ready';
      };

      paintFallbackTimer = window.setTimeout(reveal, 180);

      if ('requestVideoFrameCallback' in video) {
        video.requestVideoFrameCallback(reveal);
      } else {
        requestAnimationFrame(() => requestAnimationFrame(reveal));
      }
    }

    function applyTargetTime(now) {
      seekFrame = 0;
      if (!video || mediaError || !scrubEnabled || duration <= 0 || video.readyState < 1) return;

      const delta = Math.abs(video.currentTime - targetTime);
      if (delta <= 0.018) {
        revealPaintedFrame();
        return;
      }

      if (now - lastSeekAt < 42) {
        requestSeek();
        return;
      }

      lastSeekAt = now;
      root.dataset.motionState = ready ? 'scrubbing' : 'seeking';

      try {
        video.currentTime = targetTime;
      } catch (_) {
        root.dataset.motionState = 'seek-pending';
      }
    }

    function requestSeek() {
      if (mediaError || seekFrame) return;
      const generation = playbackGeneration;
      seekFrame = requestAnimationFrame((now) => {
        if (generation !== playbackGeneration) return;
        applyTargetTime(now);
      });
    }

    function updateProgress() {
      progressFrame = 0;

      const metrics = updateMetrics();
      const heroProgress = getHeroProgress(metrics);
      applyGeometry(metrics);
      setVisualProgress(heroProgress);

      if (ready) {
        root.dataset.motionState = 'ready';
      }

      if (duration > 0) {
        const safeStart = 0.04;
        const scrubEnd = Math.max(safeStart, Math.min(9, duration - 0.08));
        targetTime = safeStart + heroProgress * (scrubEnd - safeStart);
        requestSeek();
      }
    }

    function requestUpdate() {
      if (!errorFrame && !progressFrame) progressFrame = requestAnimationFrame(updateProgress);
    }

    function invalidatePendingVideoWork() {
      playbackGeneration += 1;
      if (seekFrame) cancelAnimationFrame(seekFrame);
      seekFrame = 0;
      if (paintFallbackTimer) window.clearTimeout(paintFallbackTimer);
      paintFallbackTimer = 0;
      if (retryTimer) window.clearTimeout(retryTimer);
      retryTimer = 0;
    }

    function clearMediaError() {
      if (errorFrame) cancelAnimationFrame(errorFrame);
      errorFrame = 0;
      mediaError = false;
      root.classList.remove('is-media-error');
      root.style.removeProperty('--hero-error-scroll-used');
    }

    function showMediaError() {
      // Stop old video callbacks immediately, but measure before the layout write.
      mediaError = true;
      ready = false;
      invalidatePendingVideoWork();
      if (video) video.pause();
      if (progressFrame) cancelAnimationFrame(progressFrame);
      progressFrame = 0;
      const generation = playbackGeneration;

      errorFrame = requestAnimationFrame(() => {
        errorFrame = 0;
        if (!mediaError || generation !== playbackGeneration) return;

        if (stage && scrubEnabled) {
          const metrics = getScrollMetrics();
          const rootRect = root.getBoundingClientRect();
          const stageRect = stage.getBoundingClientRect();
          // Preserve the currently rendered stage. Only remove trailing distance
          // below it, never consumed distance or content above the viewport.
          const remainingHeight = stageRect.bottom <= metrics.top
            ? rootRect.height
            : Math.min(rootRect.height, Math.max(stageRect.height, Math.ceil(stageRect.bottom - rootRect.top)));
          const consumedDistance = Math.max(0, remainingHeight - stageRect.height);
          setRootProperty('--hero-error-scroll-used', `${consumedDistance.toFixed(2)}px`);
        }

        root.classList.remove('is-video-ready', 'is-static');
        root.classList.add('is-media-error');
        root.dataset.motionState = 'error';
        requestUpdate();
      });
    }

    function showStaticPoster(state = 'static') {
      invalidatePendingVideoWork();
      clearMediaError();
      if (video) video.pause();
      ready = false;
      root.classList.remove('is-video-ready');
      root.classList.add('is-static');
      root.dataset.motionState = state;
    }

    function resetVideoForReload() {
      invalidatePendingVideoWork();
      clearMediaError();
      ready = false;
      duration = 0;
      loadStarted = false;
      root.classList.remove('is-video-ready', 'is-static');
      if (video) {
        video.pause();
        video.removeAttribute('src');
        video.load();
      }
    }

    function loadVideo() {
      const source = sourceForViewport();
      if (loadStarted || !video || !source || reducedQuery.matches || constrainedNetwork) return;

      loadStarted = true;
      root.dataset.motionState = 'loading';
      video.preload = 'auto';
      video.muted = true;
      video.defaultMuted = true;
      video.src = source;
      video.load();
    }

    function onVideoError() {
      if (mediaError) return;
      if (retryCount < 1 && sourceForViewport()) {
        retryCount += 1;
        root.dataset.motionState = 'retrying';
        resetVideoForReload();
        const generation = playbackGeneration;
        retryTimer = window.setTimeout(() => {
          retryTimer = 0;
          if (generation === playbackGeneration && !mediaError) loadVideo();
        }, 480);
        return;
      }

      showMediaError();
    }

    function onMotionPreferenceChange(event) {
      if (event.matches) {
        showStaticPoster('reduced-motion');
        return;
      }

      resetVideoForReload();
      retryCount = 0;
      requestMetricsUpdate();
      loadVideo();
    }

    function onVideoViewportChange() {
      if (!video) return;
      const nextSource = sourceForViewport();
      const activeSource = video.getAttribute('src') || '';
      if (!nextSource || nextSource === activeSource) return;

      resetVideoForReload();
      retryCount = 0;
      requestMetricsUpdate();
      loadVideo();
    }

    function destroy() {
      invalidatePendingVideoWork();
      clearMediaError();
      if (observer) observer.disconnect();
      if (resizeObserver) resizeObserver.disconnect();
      if (progressFrame) cancelAnimationFrame(progressFrame);
      if (seekFrame) cancelAnimationFrame(seekFrame);
      if (retryTimer) window.clearTimeout(retryTimer);
      if (paintFallbackTimer) window.clearTimeout(paintFallbackTimer);
      disposers.forEach((dispose) => dispose());
      if (video) video.pause();
      root.style.removeProperty('--hero-progress');
      root.style.removeProperty('--hero-cue-opacity');
      root.style.removeProperty('--hero-sticky-top');
      delete root.dataset.motionReady;
      delete root.dataset.motionState;
      delete root.dataset.motionScrollSurface;
      instances.delete(root);
    }

    instances.set(root, destroy);

    if (video) {
      listen(video, 'loadedmetadata', () => {
        duration = Number.isFinite(video.duration) ? video.duration : 0;
        requestUpdate();
      });
      listen(video, 'loadeddata', () => {
        requestUpdate();
        requestSeek();
        revealPaintedFrame();
      });
      listen(video, 'canplay', () => {
        requestUpdate();
        requestSeek();
        revealPaintedFrame();
      });
      listen(video, 'seeked', () => {
        if (Math.abs(video.currentTime - targetTime) > 0.018) {
          requestSeek();
        } else {
          revealPaintedFrame();
        }
      });
      listen(video, 'error', onVideoError);
    }

    listen(reducedQuery, 'change', onMotionPreferenceChange);
    listen(mobileVideoQuery, 'change', onVideoViewportChange);
    listen(window, 'scroll', requestUpdate, { passive: true });
    if (pageWrapper) listen(pageWrapper, 'scroll', requestUpdate, { passive: true });
    listen(window, 'resize', requestMetricsUpdate, { passive: true });
    listen(window, 'pageshow', requestMetricsUpdate, { passive: true });
    listen(window, 'load', requestMetricsUpdate, { passive: true, once: true });

    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(requestMetricsUpdate);
      resizeObserver.observe(root);
      if (stage) resizeObserver.observe(stage);
      if (pageWrapper) resizeObserver.observe(pageWrapper);
    }

    if (!video || !sourceForViewport()) {
      showStaticPoster('missing-video');
      return;
    }

    if (reducedQuery.matches) {
      showStaticPoster('reduced-motion');
      return;
    }

    if (constrainedNetwork) {
      showStaticPoster('save-data');
      return;
    }

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        observer = null;
        loadVideo();
      }, { root: scrollSurface === window ? null : scrollSurface, rootMargin: '400px 0px' });
      observer.observe(root);
    } else {
      loadVideo();
    }

    requestUpdate();
  }

  function rootsIn(container) {
    const roots = [];
    if (container.matches && container.matches(SELECTOR)) roots.push(container);
    if (container.querySelectorAll) roots.push(...container.querySelectorAll(SELECTOR));
    return roots;
  }

  function initAll(container = document) {
    rootsIn(container).forEach(initHero);
  }

  function destroyAll(container = document) {
    rootsIn(container).forEach((root) => instances.get(root)?.());
  }

  window[GLOBAL] = { initAll, destroyAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyAll(event.target));
})();
